import React, { useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/common/ui/Button';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info';
    loading?: boolean;
    icon?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 onConfirm,
                                                                 title,
                                                                 description,
                                                                 confirmText = 'Confirm',
                                                                 cancelText = 'Cancel',
                                                                 variant = 'danger',
                                                                 loading = false,
                                                                 icon
                                                             }) => {
    // Close modal on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !loading) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose, loading]);

    // Handle confirm action
    const handleConfirm = () => {
        if (!loading) {
            onConfirm();
        }
    };

    // Handle close action
    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    if (!isOpen) return null;

    // Get variant styles with proper dark mode support
    const getVariantStyles = () => {
        switch (variant) {
            case 'danger':
                return {
                    iconBg: 'bg-red-50 dark:bg-red-950/30',
                    iconColor: 'text-red-600 dark:text-red-400',
                    confirmButton: 'danger' as const
                };
            case 'warning':
                return {
                    iconBg: 'bg-yellow-50 dark:bg-yellow-950/30',
                    iconColor: 'text-yellow-600 dark:text-yellow-400',
                    confirmButton: 'secondary' as const
                };
            case 'info':
                return {
                    iconBg: 'bg-blue-50 dark:bg-blue-950/30',
                    iconColor: 'text-blue-600 dark:text-blue-400',
                    confirmButton: 'primary' as const
                };
            default:
                return {
                    iconBg: 'bg-red-50 dark:bg-red-950/30',
                    iconColor: 'text-red-600 dark:text-red-400',
                    confirmButton: 'danger' as const
                };
        }
    };

    const styles = getVariantStyles();
    const defaultIcon = <AlertTriangle className="h-6 w-6" />;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop - Enhanced for dark mode */}
            <div
                className="fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-md bg-background-light dark:bg-background-dark-secondary rounded-lg shadow-xl border border-border-light dark:border-border-dark-subtle">
                    {/* Header */}
                    <div className="flex items-start p-6">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-12 h-12 ${styles.iconBg} rounded-full flex items-center justify-center mr-4`}>
                            <div className={styles.iconColor}>
                                {icon || defaultIcon}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                                {title}
                            </h3>
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                                {description}
                            </p>
                        </div>

                        {/* Close button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleClose}
                            disabled={loading}
                            className="flex-shrink-0 ml-2 text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 px-6 pb-6 pt-0 border-t border-border-light dark:border-border-dark-subtle">
                        <Button
                            variant="ghost"
                            onClick={handleClose}
                            disabled={loading}
                            className="text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark"
                        >
                            {cancelText}
                        </Button>
                        <Button
                            variant={styles.confirmButton}
                            onClick={handleConfirm}
                            loading={loading}
                        >
                            {confirmText}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;